import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ContextualMenu, IContextualMenuItem, Icon } from "@fluentui/react";
import { UserIdentityHeader } from "./UserIdentityHeader";
import { getInitials } from "./userIdentity";
import styles from "./UserMenu.module.css";

export type UserMenuProps = {
  displayName: string;
  email: string;
  onSignOut: () => void;
};

/**
 * Product UserMenu, adapted for this package: identity header, divider, Sign out.
 * Workspace, settings, full-width and admin items are dropped (DP-CC-15).
 */
export function UserMenu({ displayName, email, onSignOut }: UserMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const renderMenuItem = (
    iconName: string,
    text: string,
    onClick: () => void,
    dismissMenu: (dismissAll?: boolean) => void,
  ) => (
    <div
      className={styles.menuItemRow}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        dismissMenu();
        setTimeout(() => onClick(), 0);
      }}
    >
      <span className={styles.iconColumn}>
        <Icon iconName={iconName} styles={{ root: { fontSize: 16 } }} aria-hidden="true" />
      </span>
      <span className={styles.labelColumn}>{text}</span>
    </div>
  );

  const menuItems: IContextualMenuItem[] = [
    {
      key: "userIdentity",
      text: "Account",
      onRender: () => <UserIdentityHeader displayName={displayName} email={email} />,
    },
    { key: "userIdentityDivider", itemType: 1 },
    {
      key: "logout",
      text: "Sign out",
      iconProps: { iconName: "SignOut" },
      onRender: (_item, dismissMenu) =>
        renderMenuItem("SignOut", "Sign out", onSignOut, dismissMenu || (() => undefined)),
    },
  ];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.userButton}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Open account menu"
        title={displayName || email || "Account menu"}
        data-testid="console-user-menu"
      >
        {getInitials(displayName, email)}
      </button>
      {menuOpen &&
        createPortal(
          <ContextualMenu
            target={buttonRef.current}
            items={menuItems}
            onDismiss={() => setMenuOpen(false)}
            directionalHint={6}
            calloutProps={{
              styles: {
                root: { border: "none" },
                calloutMain: {
                  border: "none",
                  paddingLeft: 12,
                  paddingRight: 8,
                  paddingTop: 8,
                  paddingBottom: 8,
                },
              },
            }}
          />,
          document.body,
        )}
    </>
  );
}
